// src/hooks/useSmartReadLength.js
import { useEffect, useState, useCallback } from "react";

export const useSmartReadLength = () => {
  const [userModifiedFields, setUserModifiedFields] = useState({});

  // Check if current min/max read lengths are non-standard
  const checkNonStandardReadlens = useCallback((current_min_readlen, current_max_readlen) => {
    // Standard min read length for riboseq/disomeseq is 20/45 respectively
    let standard_min_readlens = [20, 45];
    // Standard max read length for riboseq/disomeseq is 35/65 respectively
    let standard_max_readlens = [35, 65];
    // If the current min or max read length is non-standard (meaning user has changed it manually)
    // then do not change anything as this would be more frustrating than helpful
    if (!standard_min_readlens.includes(current_min_readlen) || !standard_max_readlens.includes(current_max_readlen)) {
      return true;
    }
    return false;
  }, []);

  // Returns a min/max read length depending on the seq types selected
  // if "heatmap" passed as the plotType will return 20/50 instead of 20/35
  const getStandardReadLengths = useCallback((selectedFiles, allFiles, plotType = null) => {
    if (!selectedFiles.length || !allFiles.length) return { min: 20, max: 35 };

    const selectedFileTypes = selectedFiles
      .map((fileId) => {
        const file = allFiles.find((f) => f.file_id === fileId);
        return file?.file_type;
      })
      .filter(Boolean);

    const hasRiboSeq = selectedFileTypes.includes("riboseq");
    const hasDisomeSeq = selectedFileTypes.includes("disomeseq");

    if (hasRiboSeq && hasDisomeSeq) {
      return { min: 20, max: 65 }; // Mixed
    } else if (hasDisomeSeq) {
      return { min: 45, max: 65 }; // Disome-seq only
    } else if (plotType == "heatmap") {
      return { min: 20, max: 50 }; // Ribo-seq only specific to heatmap on dataquality page
    } else {
      return { min: 20, max: 35 }; // Ribo-seq only (default)
    }
  }, []);

  const markFieldAsUserModified = useCallback((fieldId) => {
    setUserModifiedFields((prev) => ({ ...prev, [fieldId]: true }));
  }, []);

  const resetUserModifications = useCallback(() => {
    setUserModifiedFields({});
  }, []);

  const shouldAutoAdjust = useCallback(
    (fieldName, currentMinRead, currentMaxRead) => {
      // Don't auto-adjust if user has manually modified the field
      // Don't auto-adjust if user has manually modified the field
      if (userModifiedFields[fieldName]) {
        return false;
      }

      // Only check for non-standard values if the user has modified the fields
      // This allows auto-adjustment to fix non-standard default values
      const hasUserModifiedReadLengths = userModifiedFields.minread ||
                                        userModifiedFields.maxread ||
                                        userModifiedFields.minRead ||
                                        userModifiedFields.maxRead ||
                                        userModifiedFields.minReadLength ||
                                        userModifiedFields.maxReadLength ||
                                        userModifiedFields.minReadLen ||
                                        userModifiedFields.maxReadLen;

      if (hasUserModifiedReadLengths && currentMinRead !== undefined && currentMaxRead !== undefined) {
        const isNonStandard = checkNonStandardReadlens(currentMinRead, currentMaxRead);
        if (isNonStandard) {
          return false;
        }
      }

      return true;
    },
    [userModifiedFields, checkNonStandardReadlens]
  );

  return {
    getStandardReadLengths,
    markFieldAsUserModified,
    resetUserModifications,
    shouldAutoAdjust,
    userModifiedFields,
    checkNonStandardReadlens,
  };
};
